/**
 * Centralized logging utility
 * Replaces console.log/error/warn with a proper logging system
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
	[key: string]: unknown;
}

class Logger {
	private isDevelopment = process.env.NODE_ENV === 'development';
	private isProduction = process.env.NODE_ENV === 'production';

	private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
		const timestamp = new Date().toISOString();
		const contextStr = context ? ` ${JSON.stringify(context)}` : '';
		return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
	}

	private log(level: LogLevel, message: string, context?: LogContext): void {
		const formattedMessage = this.formatMessage(level, message, context);

		// In development, always log to console
		if (this.isDevelopment) {
			switch (level) {
				case 'error':
					console.error(formattedMessage);
					break;
				case 'warn':
					console.warn(formattedMessage);
					break;
				case 'debug':
					console.debug(formattedMessage);
					break;
				default:
					console.log(formattedMessage);
			}
		}

		// In production, only log errors and warnings
		if (this.isProduction) {
			if (level === 'error' || level === 'warn') {
				// TODO: Integrate with error tracking service (e.g., Sentry)
				// For now, still log to console in production for critical errors
				if (level === 'error') {
					console.error(formattedMessage);
				}
			}
		}
	}

	info(message: string, context?: LogContext): void {
		this.log('info', message, context);
	}

	warn(message: string, context?: LogContext): void {
		this.log('warn', message, context);
	}

	error(message: string, error?: Error | unknown, context?: LogContext): void {
		const errorMessage = error instanceof Error ? error.message : String(error);
		const errorContext = error instanceof Error 
			? { ...context, stack: error.stack, name: error.name }
			: { ...context, error: errorMessage };
		
		this.log('error', errorMessage, errorContext);
	}

	debug(message: string, context?: LogContext): void {
		if (this.isDevelopment) {
			this.log('debug', message, context);
		}
	}
}

// Export singleton instance
export const logger = new Logger();

// Export default for convenience
export default logger;

